import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { usePage } from '../../hooks/usePage';
import { rolesApi, CreateRoleDto } from '../../api/roles';
import { AdminRoleSummary, AdminPermissionDef } from '../../types';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Spinner } from '../../components/ui/Spinner';
import { Modal } from '../../components/ui/Modal';
import { Input } from '../../components/ui/Input';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import { useToast } from '../../hooks/useToast';
import { Shield, Pencil, Trash2, Plus } from 'lucide-react';

const WILDCARD = '*';

type Editing =
  | { mode: 'create' }
  | { mode: 'edit'; role: AdminRoleSummary }
  | null;

export const Roles: React.FC = () => {
  usePage('Roles & Permissions');
  const toast = useToast();

  const [roles, setRoles] = useState<AdminRoleSummary[]>([]);
  const [catalogue, setCatalogue] = useState<AdminPermissionDef[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<Editing>(null);
  const [pendingDelete, setPendingDelete] = useState<AdminRoleSummary | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  // Builder form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [r, c] = await Promise.all([
        rolesApi.getRoles(),
        rolesApi.getPermissions(),
      ]);
      setRoles(r ?? []);
      setCatalogue(c ?? []);
    } catch (err) {
      toast.error(err, 'Failed to load roles.');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  const grouped = useMemo(() => {
    const map = new Map<string, AdminPermissionDef[]>();
    for (const p of catalogue) {
      if (!map.has(p.group)) map.set(p.group, []);
      map.get(p.group)!.push(p);
    }
    return Array.from(map.entries());
  }, [catalogue]);

  const openCreate = () => {
    setName('');
    setDescription('');
    setSelected(new Set());
    setEditing({ mode: 'create' });
  };

  const openEdit = (role: AdminRoleSummary) => {
    setName(role.name);
    setDescription(role.description ?? '');
    // Wildcard roles get every permission pre-checked for editing clarity.
    setSelected(
      new Set(
        role.permissions.includes(WILDCARD)
          ? catalogue.map((p) => p.key)
          : role.permissions,
      ),
    );
    setEditing({ mode: 'edit', role });
  };

  const toggle = (key: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const toggleGroup = (perms: AdminPermissionDef[]) =>
    setSelected((prev) => {
      const next = new Set(prev);
      const allOn = perms.every((p) => next.has(p.key));
      perms.forEach((p) => (allOn ? next.delete(p.key) : next.add(p.key)));
      return next;
    });

  const isWildcardRole =
    editing?.mode === 'edit' && editing.role.permissions.includes(WILDCARD);

  const save = async () => {
    if (!name.trim()) {
      toast.warning('Give the role a name.');
      return;
    }
    if (selected.size === 0 && !isWildcardRole) {
      toast.warning('Select at least one permission.');
      return;
    }
    try {
      setSaving(true);
      // For the wildcard (Super Admin) role, preserve the wildcard rather than
      // expanding it into the full list.
      const permissions = isWildcardRole ? [WILDCARD] : Array.from(selected);
      if (editing?.mode === 'create') {
        const payload: CreateRoleDto = {
          name: name.trim(),
          description: description.trim() || undefined,
          permissions,
        };
        await rolesApi.createRole(payload);
        toast.success('Role created.');
      } else if (editing?.mode === 'edit') {
        await rolesApi.updateRole(editing.role.id, {
          name: editing.role.isSystem ? undefined : name.trim(),
          description: description.trim(),
          permissions,
        });
        toast.success('Role updated.');
      }
      setEditing(null);
      void fetchData();
    } catch (err) {
      toast.error(err, 'Could not save the role.');
    } finally {
      setSaving(false);
    }
  };

  const executeDelete = async () => {
    if (!pendingDelete) return;
    try {
      setActionLoading(true);
      await rolesApi.deleteRole(pendingDelete.id);
      toast.success(`Role "${pendingDelete.name}" deleted.`);
      setPendingDelete(null);
      void fetchData();
    } catch (err) {
      toast.error(err, 'Could not delete role.');
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <>
      <Card padding={20}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 20,
            flexWrap: 'wrap',
            gap: 12,
          }}
        >
          <p style={{ fontSize: 13, color: 'var(--neutral-500)' }}>
            {roles.length} role{roles.length !== 1 ? 's' : ''} · permissions
            control what each admin can access
          </p>
          <Button size="sm" onClick={openCreate}>
            <Plus size={15} style={{ marginRight: 6 }} />
            New Role
          </Button>
        </div>

        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
            <Spinner size={24} />
          </div>
        ) : (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 300px), 1fr))',
              gap: 16,
            }}
          >
            {roles.map((role) => {
              const count = role.permissions.includes(WILDCARD)
                ? catalogue.length || 'All'
                : role.permissions.length;
              return (
                <div
                  key={role.id}
                  style={{
                    border: '1px solid var(--neutral-300)',
                    borderRadius: 'var(--radius-md)',
                    padding: 16,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 10,
                    background: 'var(--neutral-100)',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span
                      style={{
                        width: 34,
                        height: 34,
                        borderRadius: 'var(--radius-md)',
                        background: 'var(--blue-100)',
                        color: 'var(--blue-700)',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <Shield size={18} />
                    </span>
                    <div style={{ minWidth: 0 }}>
                      <div
                        style={{
                          fontWeight: 700,
                          color: 'var(--neutral-800)',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                        }}
                      >
                        {role.name}
                        {role.isSystem && (
                          <Badge variant="info">System</Badge>
                        )}
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--neutral-500)' }}>
                        {role.adminCount} admin{role.adminCount !== 1 ? 's' : ''}
                      </div>
                    </div>
                  </div>

                  {role.description && (
                    <p style={{ fontSize: 13, color: 'var(--neutral-600)', margin: 0 }}>
                      {role.description}
                    </p>
                  )}

                  <div style={{ fontSize: 12, color: 'var(--neutral-500)' }}>
                    {role.permissions.includes(WILDCARD)
                      ? 'Full access (all permissions)'
                      : `${count} permission${count !== 1 ? 's' : ''}`}
                  </div>

                  <div style={{ display: 'flex', gap: 8, marginTop: 'auto', paddingTop: 6 }}>
                    <Button size="sm" variant="secondary" onClick={() => openEdit(role)}>
                      <Pencil size={14} style={{ marginRight: 6 }} />
                      Edit
                    </Button>
                    {!role.isSystem && (
                      <Button
                        size="sm"
                        variant="danger"
                        onClick={() => setPendingDelete(role)}
                        disabled={role.adminCount > 0}
                        title={
                          role.adminCount > 0
                            ? 'Reassign its admins before deleting'
                            : undefined
                        }
                      >
                        <Trash2 size={14} />
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* Builder Modal */}
      <Modal
        isOpen={editing !== null}
        onClose={() => setEditing(null)}
        title={editing?.mode === 'edit' ? 'Edit Role' : 'New Role'}
        maxWidth={620}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <Input
            label="Role name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Disputes Team"
            disabled={editing?.mode === 'edit' && editing.role.isSystem}
          />
          <Input
            label="Description (optional)"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="What this role is for"
          />

          {isWildcardRole && (
            <div
              style={{
                padding: 12,
                background: 'var(--blue-100)',
                border: '1px solid var(--blue-300)',
                borderRadius: 'var(--radius-md)',
                fontSize: 13,
                color: 'var(--blue-800)',
              }}
            >
              This is the full-access role. It always grants every permission,
              including ones added in the future.
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: 14, opacity: isWildcardRole ? 0.6 : 1 }}>
            <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--neutral-700)' }}>
              Permissions
            </label>
            {grouped.map(([group, perms]) => {
              const allOn = perms.every((p) => selected.has(p.key));
              return (
                <div key={group}>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: 6,
                    }}
                  >
                    <span
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        color: 'var(--neutral-600)',
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                      }}
                    >
                      {group}
                    </span>
                    <button
                      type="button"
                      onClick={() => toggleGroup(perms)}
                      disabled={isWildcardRole}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: 'var(--blue-600)',
                        fontSize: 12,
                        fontWeight: 600,
                        cursor: isWildcardRole ? 'default' : 'pointer',
                      }}
                    >
                      {allOn ? 'Clear' : 'Select all'}
                    </button>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {perms.map((p) => (
                      <label
                        key={p.key}
                        style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 10,
                          padding: '8px 10px',
                          borderRadius: 'var(--radius-md)',
                          background: selected.has(p.key)
                            ? 'var(--blue-100)'
                            : 'var(--neutral-200)',
                          cursor: isWildcardRole ? 'default' : 'pointer',
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={selected.has(p.key)}
                          disabled={isWildcardRole}
                          onChange={() => toggle(p.key)}
                          style={{ marginTop: 2 }}
                        />
                        <span>
                          <span
                            style={{
                              display: 'block',
                              fontSize: 13,
                              fontWeight: 600,
                              color: 'var(--neutral-800)',
                            }}
                          >
                            {p.label}
                          </span>
                          <span style={{ fontSize: 12, color: 'var(--neutral-500)' }}>
                            {p.description}
                          </span>
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button variant="ghost" onClick={() => setEditing(null)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={save} loading={saving}>
              {editing?.mode === 'edit' ? 'Save Changes' : 'Create Role'}
            </Button>
          </div>
        </div>
      </Modal>

      <ConfirmDialog
        isOpen={pendingDelete !== null}
        title="Delete role?"
        message={`"${pendingDelete?.name}" will be permanently removed. This cannot be undone.`}
        confirmLabel="Delete"
        variant="danger"
        loading={actionLoading}
        onConfirm={executeDelete}
        onCancel={() => setPendingDelete(null)}
      />
    </>
  );
};
